/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import {
    Button,
    Card,
    DatePicker,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useSearchParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import ListState from "../../../../components/ListState/ListState";
import Navbar from "../../../../components/Navbar/Navbar";
import { useGetConfirmationReports } from "../../hooks/queries/reports.queries";
import { IConfirmationReport } from "../../types/reports.types";
import {
    convertDateFilterStringToDate,
    convertTimeTo12HoursFormat,
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import { RecurrenceRule } from "../../../../utils/types";
import { useDebounce } from "../../../../hooks/helpers";
import { useFetchClientList } from "../../../../hooks/queries/client";
import { useFetchProviders } from "../../../providers/hooks/providers.queries";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import Loader from "../../../../shared-ui/Loader/Loader";

export default function ConfirmationReportsList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const searchString = searchParams.get("search") || "";
    const [clientSearchString, setClientSearchString] = React.useState("");
    const [providerSearchString, setProviderSearchString] = React.useState("");

    const getSessionStatusDisplay = (row: IConfirmationReport) => {
        if (
            row.session_status === "inactive" &&
            row.session_metadata?.is_paused
        ) {
            return "Paused";
        }
        return removeEnumUnderscore(row.session_status);
    };

    const [showClientListPopover, setShowClientListPopover] =
        React.useState(false);
    const [showProviderListPopover, setShowProviderListPopover] =
        React.useState(false);

    const clientFilter = searchParams.get("client_id") || "";
    const providerFilter = searchParams.get("provider_id") || "";
    const confirmedStartDateFilter = searchParams.get(
        "confirmed_start_date_time"
    );
    const confirmedEndDateFilter = searchParams.get("confirmed_end_date_time");
    const recurrenceFilter = searchParams.get("recurrence_rule") || "";
    const sessionStatusFilter = searchParams.get("session_status") || "active";
    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;

    const sortAttributeFilter = searchParams.get("sort_attr") || "";
    const sortOrderFilter = searchParams.get("sort_order") || "";

    const [sortConfig, setSortConfig] = React.useState<{
        key: string;
        direction: string;
    }>({
        key: sortAttributeFilter,
        direction: sortOrderFilter,
    });

    const search = useDebounce(searchString, 500);
    const debouncedProviderSearchValue = useDebounce(providerSearchString, 500);
    const debouncedClientSearchValue = useDebounce(clientSearchString, 500);

    const {
        data: clients,
        isLoading: clientsLoading,
        error: clientsError,
    } = useFetchClientList({
        page: 1,
        search_string: debouncedClientSearchValue,
    });

    const {
        data: providers,
        isLoading: providerListLoading,
        error: providerListError,
    } = useFetchProviders({
        page: 1,
        search_string: debouncedProviderSearchValue,
        limit: 20,
    });

    const { data, error, isLoading, isSuccess } = useGetConfirmationReports({
        search_string: search,
        confirmed_start_date_time: confirmedStartDateFilter,
        confirmed_end_date_time: confirmedEndDateFilter,
        recurrence_rule: recurrenceFilter,
        client_id: clientFilter,
        provider_id: providerFilter,
        session_status: sessionStatusFilter,
        page: pageFilter,
        limit: limitFilter,
        sort_attribute: sortConfig.key || sortAttributeFilter,
        sort_order: sortConfig.direction || sortOrderFilter,
    });

    const getClientFilterTextToDisplay = React.useMemo(() => {
        const selectedClient = clients?.data.find(
            (client) => client.client_id === clientFilter
        );
        if (!selectedClient) return null;

        return displayNameInRightFormat({
            firstName: selectedClient.first_name,
            lastName: selectedClient.last_name,
        });
    }, [clientFilter, clients?.data]);

    const getProviderFilterTextToDisplay = React.useMemo(() => {
        const selectedProvider = providers?.data.find(
            (provider) => provider.provider_id === providerFilter
        );
        if (!selectedProvider) return null;

        return displayNameInRightFormat(selectedProvider.provider_name);
    }, [providerFilter, providers?.data]);

    const handleSetSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === "") searchParams.delete("search");
        else searchParams.set("search", e.target.value);

        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleConfirmedStartDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("confirmed_start_date_time");
        else
            searchParams.set(
                "confirmed_start_date_time",
                format(date, "yyyy-MM-dd")
            );
        searchParams.set("page", "1");

        setSearchParams(searchParams);
    };

    const handleConfirmedEndDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("confirmed_end_date_time");
        else
            searchParams.set(
                "confirmed_end_date_time",
                format(date, "yyyy-MM-dd")
            );
        searchParams.set("page", "1");

        setSearchParams(searchParams);
    };

    const handleRecurrenceChange = (value: string) => {
        if (value === "") searchParams.delete("recurrence_rule");
        else searchParams.set("recurrence_rule", value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleSessionStatusChange = (value: string) => {
        if (value === "") searchParams.delete("session_status");
        else searchParams.set("session_status", value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleClientFilter = (client_id: string) => {
        if (client_id === "") {
            searchParams.delete("client_id");
        } else {
            searchParams.set("client_id", client_id);
        }
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleProviderFilter = (provider_id: string) => {
        if (provider_id === "") {
            searchParams.delete("provider_id");
        } else {
            searchParams.set("provider_id", provider_id);
        }
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    const handleRowClick = (row: Row<IConfirmationReport>) => {
        navigate(
            `/clients/${row.original.client.client_id}?tab=sessions-history`
        );
    };

    React.useEffect(() => {
        searchParams.set("sort_attr", sortConfig.key);
        searchParams.set("sort_order", sortConfig.direction);
        localStorage.setItem(
            "confirmationreports_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortConfig]);

    const confirmedStartDateFilterToDate = convertDateFilterStringToDate(
        confirmedStartDateFilter
    );
    const confirmedEndDateFilterToDate = convertDateFilterStringToDate(
        confirmedEndDateFilter
    );

    let confirmationReportsCount;
    if (error) confirmationReportsCount = "";
    else if (isLoading) confirmationReportsCount = "";
    else if (data?.data && data?.data.length === 0)
        confirmationReportsCount = 0;
    else if (data?.data && data?.data.length > 0)
        confirmationReportsCount = data.total_count;

    const columns: ColumnDef<IConfirmationReport>[] = [
        {
            accessorKey: "client_name",
            header: "CLIENT'S NAME",
            cell: ({ row }) =>
                displayNameInRightFormat({
                    firstName: row.original.client.client_first_name,
                    lastName: row.original.client.client_last_name,
                }),
        },
        {
            accessorKey: "session_confirmed_date_on_intake",
            header: "CONFIRMATION DATE",
            cell: ({ row }) =>
                formatDate(row.original.session_confirmed_date_on_intake),
        },
        {
            accessorKey: "provider_name",
            header: "PROVIDER NAME",
            cell: ({ row }) =>
                displayNameInRightFormat({
                    firstName: row.original.provider.provider_first_name,
                    lastName: row.original.provider.provider_last_name,
                }),
        },
        {
            accessorKey: "session_start_date",
            header: "SESSION START DATE",
            cell: ({ row }) => formatDate(row.original.session_start_date),
        },
        {
            accessorKey: "session_service",
            header: "SERVICE",
            cell: ({ row }) => (
                <span className="capitalize">
                    {removeEnumUnderscore(row.original.session_service)}
                </span>
            ),
        },
        {
            accessorKey: "session_day_time",
            header: "APPOINTMENT",
            cell: ({ row }) =>
                `${row.original.session_day} ${convertTimeTo12HoursFormat(
                    `${row.original.session_start_hr}:${row.original.session_start_min}`
                )}, ${makeStringFirstLetterCapital(
                    row.original.session_recurrence_rule
                )}`,
        },
        {
            accessorKey: "session_status",
            header: "SESSION STATUS",
            cell: ({ row }) => (
                <span className="capitalize">
                    {getSessionStatusDisplay(row.original)}
                </span>
            ),
        },
    ];

    return (
        <>
            <Navbar
                title={`Confirmation Reports (${confirmationReportsCount})`}
            />

            <div className="page">
                <Card classNames="flex flex-col gap-[--spacing-items] mb-[--spacing-header]">
                    <SearchInput
                        placeholder="Search by client name, provider name, chart number"
                        containerClass="w-[400px]"
                        onChange={handleSetSearchString}
                        defaultValue={searchString}
                    />

                    <div className="flex justify-between items-center flex-wrap gap-[--spacing-items]">
                        <div className="flex gap-[--spacing-items] flex-wrap">
                            <Popover
                                open={showClientListPopover}
                                onOpenChange={setShowClientListPopover}
                            >
                                <PopoverTrigger
                                    asChild
                                    onClick={() =>
                                        setShowClientListPopover(true)
                                    }
                                >
                                    <FilterButton
                                        className={
                                            getClientFilterTextToDisplay
                                                ? "uppercase"
                                                : "normal-case"
                                        }
                                    >
                                        {(clientsLoading && "Loading..") ||
                                            (clientsError &&
                                                "Error loading clients") ||
                                            (clients?.data &&
                                            getClientFilterTextToDisplay
                                                ? truncateString(
                                                      getClientFilterTextToDisplay
                                                  )
                                                : "Client")}
                                    </FilterButton>
                                </PopoverTrigger>
                                <PopoverContent
                                    className="w-[316px]"
                                    align="start"
                                >
                                    <SearchInput
                                        placeholder="Search by client name"
                                        onChange={(e) => {
                                            setClientSearchString(
                                                e.target.value
                                            );
                                        }}
                                    />
                                    {clientsLoading && (
                                        <Loader wrapperClassName="h-[150px]" />
                                    )}
                                    {clientsError && (
                                        <ListState
                                            isError
                                            context="general"
                                            stateHelperText="Please try again later"
                                            errorMsg={
                                                clientsError?.response?.data
                                                    ?.message ||
                                                "Error loading clients"
                                            }
                                            cardClass="h-[150px]"
                                        />
                                    )}
                                    {clients && (
                                        <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                                            <Button
                                                size="auto"
                                                variant="can-hover"
                                                className="font-medium capitalize w-full justify-start my-1.5 shrink-0"
                                                onClick={() => {
                                                    handleClientFilter("");
                                                    setShowClientListPopover(
                                                        false
                                                    );
                                                }}
                                            >
                                                ALL
                                            </Button>
                                            {clients?.data?.map((client) => (
                                                <Button
                                                    key={client.client_id}
                                                    size="auto"
                                                    variant="can-hover"
                                                    className="font-medium capitalize w-full justify-start mb-1.5 shrink-0"
                                                    onClick={() => {
                                                        handleClientFilter(
                                                            client.client_id
                                                        );
                                                        setShowClientListPopover(
                                                            false
                                                        );
                                                    }}
                                                >
                                                    {displayNameInRightFormat({
                                                        firstName:
                                                            client.first_name,
                                                        lastName:
                                                            client.last_name,
                                                    })}
                                                </Button>
                                            ))}
                                        </div>
                                    )}
                                </PopoverContent>
                            </Popover>

                            <Popover
                                open={showProviderListPopover}
                                onOpenChange={setShowProviderListPopover}
                            >
                                <PopoverTrigger
                                    asChild
                                    onClick={() =>
                                        setShowProviderListPopover(true)
                                    }
                                >
                                    <FilterButton
                                        className={
                                            getProviderFilterTextToDisplay
                                                ? "uppercase"
                                                : "normal-case"
                                        }
                                    >
                                        {(providerListLoading && "Loading..") ||
                                            (providerListError &&
                                                "Error loading providers") ||
                                            (providers?.data &&
                                            getProviderFilterTextToDisplay
                                                ? truncateString(
                                                      getProviderFilterTextToDisplay
                                                  )
                                                : "Provider")}
                                    </FilterButton>
                                </PopoverTrigger>
                                <PopoverContent
                                    className="w-[316px]"
                                    align="start"
                                >
                                    <SearchInput
                                        placeholder="Search by provider name"
                                        onChange={(e) =>
                                            setProviderSearchString(
                                                e.target.value
                                            )
                                        }
                                    />
                                    {providerListLoading && (
                                        <Loader wrapperClassName="h-[150px]" />
                                    )}
                                    {providerListError && (
                                        <ListState
                                            isError
                                            context="general"
                                            stateHelperText="Please try again later"
                                            errorMsg={
                                                providerListError?.response
                                                    ?.data?.message ||
                                                "Error loading providers"
                                            }
                                            cardClass="h-[150px]"
                                        />
                                    )}
                                    {providers && (
                                        <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                                            <Button
                                                size="auto"
                                                variant="can-hover"
                                                className="font-medium capitalize w-full justify-start my-1.5 shrink-0"
                                                onClick={() => {
                                                    handleProviderFilter("");
                                                    setShowProviderListPopover(
                                                        false
                                                    );
                                                }}
                                            >
                                                ALL
                                            </Button>

                                            {providers?.data?.map(
                                                (provider) => (
                                                    <Button
                                                        key={
                                                            provider.provider_id
                                                        }
                                                        size="auto"
                                                        variant="can-hover"
                                                        className="font-medium capitalize w-full justify-start mb-1.5 shrink-0"
                                                        onClick={() => {
                                                            handleProviderFilter(
                                                                provider.provider_id
                                                            );
                                                            setShowProviderListPopover(
                                                                false
                                                            );
                                                        }}
                                                    >
                                                        {displayNameInRightFormat(
                                                            provider.provider_name
                                                        )}
                                                    </Button>
                                                )
                                            )}
                                        </div>
                                    )}
                                </PopoverContent>
                            </Popover>

                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton className="normal-case">
                                        {recurrenceFilter || "Recurrence"}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent align="end">
                                    <DropdownItem
                                        onClick={() =>
                                            handleRecurrenceChange("")
                                        }
                                        isSelected={!recurrenceFilter}
                                        closeMenuOnSelect
                                    >
                                        All
                                    </DropdownItem>
                                    {Object.values(RecurrenceRule).map(
                                        (recurrence) => (
                                            <DropdownItem
                                                key={recurrence}
                                                onClick={() =>
                                                    handleRecurrenceChange(
                                                        recurrence
                                                    )
                                                }
                                                isSelected={
                                                    recurrenceFilter ===
                                                    recurrence
                                                }
                                                itemValue={recurrence}
                                                closeMenuOnSelect
                                            >
                                                {recurrence}
                                            </DropdownItem>
                                        )
                                    )}
                                </DropdownContent>
                            </Dropdown>

                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton className="normal-case">
                                        {sessionStatusFilter
                                            ? makeStringFirstLetterCapital(
                                                  sessionStatusFilter
                                              )
                                            : "Status"}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent align="end">
                                    <DropdownItem
                                        onClick={() =>
                                            handleSessionStatusChange("active")
                                        }
                                        isSelected={
                                            sessionStatusFilter === "active"
                                        }
                                        closeMenuOnSelect
                                    >
                                        Active
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={() =>
                                            handleSessionStatusChange(
                                                "inactive"
                                            )
                                        }
                                        isSelected={
                                            sessionStatusFilter === "inactive"
                                        }
                                        closeMenuOnSelect
                                    >
                                        Inactive
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={() =>
                                            handleSessionStatusChange("paused")
                                        }
                                        isSelected={
                                            sessionStatusFilter === "paused"
                                        }
                                        closeMenuOnSelect
                                    >
                                        Paused
                                    </DropdownItem>
                                </DropdownContent>
                            </Dropdown>

                            <Popover>
                                <PopoverTrigger asChild>
                                    <FilterButton>
                                        Confirmation Date
                                    </FilterButton>
                                </PopoverTrigger>
                                <PopoverContent align="end">
                                    <div className="mb-1">
                                        <DatePicker
                                            label="From"
                                            placeholderText="MM/DD/YYYY"
                                            selected={
                                                confirmedStartDateFilterToDate
                                            }
                                            onChange={
                                                handleConfirmedStartDateFilterChange
                                            }
                                            className="hover:border-primary"
                                            maxDate={
                                                confirmedEndDateFilterToDate ||
                                                new Date(Date.now())
                                            }
                                            isClearable
                                        />
                                    </div>
                                    <DatePicker
                                        label="To"
                                        placeholderText="MM/DD/YYYY"
                                        selected={confirmedEndDateFilterToDate}
                                        onChange={
                                            handleConfirmedEndDateFilterChange
                                        }
                                        className="hover:border-primary"
                                        minDate={confirmedStartDateFilterToDate}
                                        isClearable
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>
                </Card>

                {error && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error?.response?.data.message ||
                            `Cannot display confirmation reports at this time please try again later`
                        }
                    />
                )}

                {isLoading && <Loader />}

                {data && isSuccess && data.data.length === 0 && (
                    <ListState
                        stateHelperText="Confirmation reports will appear here once sessions are confirmed"
                        isError={false}
                        emptyMessage="No confirmation reports found"
                    />
                )}

                {isSuccess && data && data.data.length > 0 && (
                    <Table
                        data={data.data}
                        columns={columns}
                        hasPagination={data.total_count > 20}
                        setSortConfig={setSortConfig}
                        sortConfig={sortConfig}
                        sortableColumns={[
                            "session_confirmed_date_on_intake",
                            "session_start_date",
                            "client_name",
                            "provider_name",
                            "session_day_time",
                        ]}
                        handleRowClick={handleRowClick}
                        pagination={
                            <Pagination
                                totalCount={data.total_count}
                                totalPages={data.total_pages}
                                currentPage={pageFilter}
                                onPageChange={handlePageChange}
                                limit={limitFilter}
                                onLimitChange={handleLimitChange}
                            />
                        }
                    />
                )}
            </div>
        </>
    );
}
